// src/components/Reports.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { FaDownload } from 'react-icons/fa';

function Reports() {
  const [predictions, setPredictions] = useState([]);
  const [statistics, setStatistics] = useState({
    totalPredictions: 0,
    xrayPneumonia: 0,
    xrayNormal: 0,
    mriGlioma: 0,
    mriMeningioma: 0,
    mriPituitary: 0,
    mriNoTumor: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate('/');
        return;
      }

      const fetchPredictions = async () => {
        setLoading(true);
        setError('');
        try {
          const predictionQuery = query(
            collection(db, 'prediction_history'),
            where('userId', '==', currentUser.uid)
          );
          const querySnapshot = await getDocs(predictionQuery);

          const predictionData = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            timestamp: doc.data().date ? new Date(doc.data().date).getTime() : 0,
            date: doc.data().date ? new Date(doc.data().date).toLocaleString() : 'N/A',
          }));

          // Sort predictions by timestamp in descending order (newest first)
          predictionData.sort((a, b) => b.timestamp - a.timestamp);

          const stats = {
            totalPredictions: predictionData.length,
            xrayPneumonia: 0,
            xrayNormal: 0,
            mriGlioma: 0,
            mriMeningioma: 0,
            mriPituitary: 0,
            mriNoTumor: 0,
          };

          predictionData.forEach((prediction) => {
            if (prediction.type === 'Chest Dx') {
              if (prediction.result === 'Pneumonia detected') stats.xrayPneumonia++;
              if (prediction.result === 'No pneumonia detected') stats.xrayNormal++;
            }
            if (prediction.type === 'Brain Dx') {
              if (prediction.result === 'Glioma detected') stats.mriGlioma++;
              if (prediction.result === 'Meningioma detected') stats.mriMeningioma++;
              if (prediction.result === 'Pituitary tumor detected') stats.mriPituitary++;
              if (prediction.result === 'No tumor detected') stats.mriNoTumor++;
            }
          });

          setPredictions(predictionData);
          setStatistics(stats);;
        } catch (err) {
          console.error('Error fetching predictions:', err.message);
          setError('Failed to fetch prediction history: ' + err.message);
        } finally {
          setLoading(false);
        }
      };

      await fetchPredictions();
    });

    return () => unsubscribe();
  }, [auth, navigate]);

  const downloadSinglePDF = async (prediction) => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('Medpredix Prediction Record', 14, 20);

    doc.setFontSize(14);
    doc.text('Prediction Details', 14, 40);
    doc.setFontSize(12);
    doc.text(`Date: ${prediction.date}`, 14, 50);
    doc.text(`Type: ${prediction.type || 'N/A'}`, 14, 60);
    doc.text(`Result: ${prediction.result || 'N/A'}`, 14, 70);
    doc.text(`Confidence: ${prediction.confidence || 'N/A'}%`, 14, 80);
    doc.text(`Name: ${prediction.name || 'Anonymous'}`, 14, 90);
    doc.text(`Label: ${prediction.labelName || 'No Label'}`, 14, 100);

    if (prediction.imageUrl) {
      try {
        const response = await fetch(prediction.imageUrl);
        if (!response.ok) throw new Error('Failed to fetch image');

        const blob = await response.blob();
        const reader = new FileReader();
        const base64Image = await new Promise((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        const imgWidth = 100;
        const imgProps = doc.getImageProperties(base64Image);
        const imgHeight = (imgProps.height / imgProps.width) * imgWidth;
        doc.text('Image:', 14, 110);
        doc.addImage(base64Image, 'JPEG', 14, 120, imgWidth, imgHeight);
      } catch (err) {
        console.error('Error embedding image in PDF:', err);
        doc.text('Image: Failed to load image', 14, 110);
      }
    } else {
      doc.text('Image: N/A', 14, 110);
    }

    doc.setFontSize(10);
    doc.text('Page 1 of 1', 180, 290);
    doc.text('Generated by Medpredix', 14, 290);

    doc.save(`prediction_${prediction.id}.pdf`);
  };

  if (loading) return <div className="p-6 text-gray-600">Loading...</div>;

  return (
    <div className="p-6 font-['Noto Sans']">
      <h2 className="text-4xl font-bold text-gray-600 mb-6">Prediction Reports</h2>
      {error && <p className="text-red-500 mb-4">{error}</p>}

      <div className="mb-8">
        <h3 className="text-2xl font-semibold text-gray-600 mb-4">Statistics</h3>
        <div className="bg-white rounded-lg shadow-md border border-gray-300 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-lg font-semibold text-gray-700">Total Predictions</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.totalPredictions}</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-semibold text-gray-700">X-ray Pneumonia</p>
              <p className="text-2xl font-bold text-red-600">{statistics.xrayPneumonia}</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-semibold text-gray-700">X-ray Normal</p>
              <p className="text-2xl font-bold text-green-600">{statistics.xrayNormal}</p>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-2xl font-semibold text-gray-600">Prediction Records</h3>
        </div>
        {predictions.length === 0 ? (
          <p className="text-gray-600">No prediction records found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-2 px-4 border-b text-left">Date</th>
                  <th className="py-2 px-4 border-b text-left">Type</th>
                  <th className="py-2 px-4 border-b text-left">Result</th>
                  <th className="py-2 px-4 border-b text-left">Confidence</th>
                  <th className="py-2 px-4 border-b text-left">Name</th>
                  <th className="py-2 px-4 border-b text-left">Label</th>
                  <th className="py-2 px-4 border-b text-left">Image</th>
                  <th className="py-2 px-4 border-b text-left">Download</th>
                </tr>
              </thead>
              <tbody>
                {predictions.map((prediction) => (
                  <tr key={prediction.id} className="hover:bg-gray-50">
                    <td className="py-2 px-4 border-b">{prediction.date}</td>
                    <td className="py-2 px-4 border-b">{prediction.type || 'N/A'}</td>
                    <td className="py-2 px-4 border-b">{prediction.result || 'N/A'}</td>
                    <td className="py-2 px-4 border-b">{prediction.confidence || 'N/A'}%</td>
                    <td className="py-2 px-4 border-b">{prediction.name || 'Anonymous'}</td>
                    <td className="py-2 px-4 border-b">{prediction.labelName || 'No Label'}</td>
                    <td className="py-2 px-4 border-b">
                      {prediction.imageUrl ? (
                        <a href={prediction.imageUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          View Image
                        </a>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td className="py-2 px-4 border-b">
                      <button
                        onClick={() => downloadSinglePDF(prediction)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Download Record as PDF"
                      >
                        <FaDownload size={20} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Reports;